import React, { PropsWithChildren, useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import Image from "next/image";

import Link from "next/link";

import Container from "components/Container";

import Play from "assets/iconsv2/play-16.svg";
import Hammer from "assets/iconsv2/hammer-16.svg";
import ArrowNe from "assets/iconsv2/arrow-ne-16.svg";
import ArrowRight from "assets/iconsv2/arrow-right-16.svg";
import MaginifyingGlass from "assets/iconsv2/magnifying-glass-16.svg";
import LinkIcon from "assets/iconsv2/link-16.svg";
import PlusMinusDivideMultiply from "assets/iconsv2/plus-minus-divide-multiply-16.svg";
import CopyClipboard from "assets/iconsv2/copy-clipboard-16.svg";
import Snippets from "assets/iconsv2/snippets-16.svg";
import AppShortcutsIcon from "assets/home/features/app-shortcuts-icon.png";
import FileSearchIcons from "assets/home/features/file-search-icons.png";
import SystemCommands from "assets/home/features/system-commands.png";
import QuicklinksImage from "assets/home/features/quicklinks.png";
import WindowManagement from "assets/home/features/window-management.png";
import LaunchApps from "assets/home/features/launch-apps.png";
import MySchedule from "assets/home/features/my-schedule.png";
import MyScheduleIcon from "assets/home/features/my-schedule-icon.png";
import MyScheduleIcons from "assets/home/features/my-schedule-icons.png";
import FloatingNotesIcon from "assets/home/floating-notes-example/floating-notes-extension-32.png";

import BlockCallToAction from "./BlockCallToAction";

import styles from "./Features.module.css";
import Subtitle from "./Subtitle";
import CalculatorExample from "./CalculatorExample";
import FloatingNotesExample from "./FloatingNotesExample";

type ResponsiveSpan = {
  sm?: number;
  lg?: number;
  md?: number;
};

type PanelPropTypes = {
  rowEndSpan?: number | ResponsiveSpan;
  colEndSpan?: number | ResponsiveSpan;
  className?: string;
};

const Panel: React.FC<PropsWithChildren<PanelPropTypes>> = ({
  rowEndSpan = 1,
  colEndSpan = 1,
  children,
  className,
}) => {
  const [isMediaLarge, setIsMediaLarge] = useState<boolean>(false);
  const [isMediaMedium, setIsMediaMedium] = useState<boolean>(false);

  const mediaLargeCallback = useCallback(
    (matchMediaResult: MediaQueryListEvent | MediaQueryList) => {
      setIsMediaLarge(matchMediaResult.matches);
    },
    [setIsMediaLarge]
  );

  const mediaMediumCallback = useCallback(
    (matchMediaResult: MediaQueryListEvent | MediaQueryList) => {
      setIsMediaMedium(matchMediaResult.matches);
    },
    [setIsMediaMedium]
  );

  useEffect(() => {
    const matchMediaLargeResult = matchMedia("(min-width: 1064px)");
    mediaLargeCallback(matchMediaLargeResult);
    matchMediaLargeResult.addEventListener("change", mediaLargeCallback);

    return () => {
      matchMediaLargeResult.removeEventListener("change", mediaLargeCallback);
    };
  }, [mediaLargeCallback]);

  useEffect(() => {
    const matchMediaMediumResult = matchMedia("(min-width: 740px)");
    mediaMediumCallback(matchMediaMediumResult);
    matchMediaMediumResult.addEventListener("change", mediaMediumCallback);

    return () => {
      matchMediaMediumResult.removeEventListener("change", mediaMediumCallback);
    };
  }, [mediaMediumCallback]);

  let calculatedRowEndSpan: number;
  let calculatedColEndSpan: number;

  if (typeof rowEndSpan !== "number") {
    if (isMediaLarge) calculatedRowEndSpan = rowEndSpan.lg || rowEndSpan.md || rowEndSpan.sm || 1;
    else if (isMediaMedium) calculatedRowEndSpan = rowEndSpan.md || rowEndSpan.sm || 1;
    else calculatedRowEndSpan = rowEndSpan.sm || 1;
  } else {
    calculatedRowEndSpan = rowEndSpan;
  }

  if (typeof colEndSpan !== "number") {
    if (isMediaLarge) calculatedColEndSpan = colEndSpan.lg || colEndSpan.md || colEndSpan.sm || 1;
    else if (isMediaMedium) calculatedColEndSpan = colEndSpan.md || colEndSpan.sm || 1;
    else calculatedColEndSpan = colEndSpan.sm || 1;
  } else {
    calculatedColEndSpan = colEndSpan;
  }

  return (
    <div
      style={{ gridRowEnd: `span ${calculatedRowEndSpan}`, gridColumnEnd: `span ${calculatedColEndSpan}` }}
      className={classNames(styles.panel, className)}
    >
      {children}
    </div>
  );
};

const Features: React.FC = () => {
  return (
    <>
      <Subtitle>
        <h3>
          Productivity,
          <br />
          levelled up.
        </h3>
        <p>Make Raycast your own, using the bundle of features available from day one.</p>
      </Subtitle>
      <Container>
        <div className={styles.panelContainer}>
          <Panel rowEndSpan={8}>
            <div className={classNames(styles.panelIcon, styles.red)}>
              <Play />
            </div>
            <strong>Script Commands</strong>
            <p>
              Write scripts in your favorite programming language to connect to web APIs, control apps, tools, and more.
            </p>

            <a
              href="https://github.com/raycast/script-commands"
              className={styles.scriptCommandsCallout}
              target="_blank"
              rel="noreferrer"
            >
              Check out more than 600 scripts from the community
              <i>
                <ArrowNe className="icon" />
              </i>
            </a>
          </Panel>
          <Panel rowEndSpan={6}>
            <div className={styles.panelIcon}>
              <Image src={AppShortcutsIcon} width={32} height={32} layout="fixed" alt="Apple Shortcuts Icon" />
            </div>
            <strong>Shortcuts</strong>
            <p>
              Search and run your macOS Shortcuts directly from Raycast. Keeping everything in one place for fast
              access.
            </p>
          </Panel>
          <Panel rowEndSpan={8}>
            <div className={classNames(styles.panelIcon, styles.red)}>
              <MaginifyingGlass />
            </div>
            <strong>File Search</strong>
            <p>Search for files and folders the simple way, view metadata, and perform common actions.</p>
            <Image src={FileSearchIcons} alt="4 greyed out file icons" width={224.5} height={40} layout="fixed" />
          </Panel>
          <Panel rowEndSpan={11}>
            <strong>System Commands</strong>
            <p>
              Control your Mac with over 20 system commands built in. From turning up or down volume, to emptying your
              trash, sleep or restarting your system, and much more.
            </p>
            <div className={styles.panelBleedBottom}>
              <Image src={SystemCommands} layout="responsive" alt="A list of System Command icons" />
            </div>
          </Panel>
          <Panel rowEndSpan={12} className={styles.floatingNotes}>
            <div className={styles.example}>
              <FloatingNotesExample />
            </div>
            <div className={styles.text}>
              <div className={styles.panelIcon}>
                <Image src={FloatingNotesIcon} width={32} height={32} layout="fixed" alt="Letter T" />
              </div>
              <strong>Floating Notes</strong>
              <p>
                Floating Notes are a quick way to jot down a thought while working on something else. Always handy when
                you need it.
              </p>
            </div>
          </Panel>
          <Panel rowEndSpan={12}>
            <div className={classNames(styles.panelIcon, styles.red)}>
              <LinkIcon />
            </div>
            <strong>Quicklinks</strong>
            <p>
              Open links in the browser, folders in the terminal or projects in any text editor. And search the web much
              more efficient.
            </p>
            <p>Use queries to perform searches on Google, Dribbble or other websites, like below.</p>
            <div className={styles.panelBleedBottom} style={{ height: 152 }}>
              <div className={styles.quicklinkExample}>
                <Image src={QuicklinksImage} width={679} height={425} alt="Example of a Quicklink" layout="fixed" />
              </div>
            </div>
          </Panel>
          <Panel rowEndSpan={3} className={styles.buildYourOwn}>
            <Link href="/developers">
              <a>
                <div className={classNames(styles.panelIcon, styles.green)}>
                  <Hammer />
                </div>
                <span>
                  Build your own
                  <i>
                    <ArrowRight />
                  </i>
                </span>
              </a>
            </Link>
          </Panel>
          <Panel rowEndSpan={{ sm: 16, lg: 10 }} colEndSpan={{ lg: 3, md: 2 }} className={styles.calculator}>
            <div className={styles.text}>
              <div className={classNames(styles.panelIcon, styles.red)}>
                <PlusMinusDivideMultiply />
              </div>
              <strong>Calculator</strong>
              <p>
                Solve simple math expressions, convert units, check timezones, calculate differences between days,
                months, and years, and do much more in natural language. Copy the answer to your clipboard to carry on
                with what you were doing before.
              </p>
              <span className={styles.sub}>
                Crypto Market Cap &amp; Pricing Data Provided By{" "}
                <a href="https://nomics.com/" target="_blank" rel="noopener noreferrer">
                  Nomics
                </a>
              </span>
            </div>
            <div className={styles.example}>
              <CalculatorExample />
            </div>
          </Panel>
          <Panel rowEndSpan={{ sm: 10, md: 6 }} colEndSpan={{ md: 2 }} className={styles.windowManagement}>
            <div className={styles.text}>
              <strong>Window Management</strong>
              <p>
                Clean up your window mess with handy commands for organizing your desktop on the fly and resizing app
                windows.
              </p>
            </div>
            <div className={styles.image}>
              <div className={styles.imageWrapper}>
                <Image
                  src={WindowManagement}
                  width={290}
                  height={487.5}
                  alt="Tiled list of window management icons showing the different options Raycast allows for window management"
                />
              </div>
            </div>
          </Panel>
          <Panel rowEndSpan={13} className={styles.launchApps}>
            <div className={styles.text}>
              <strong>Launch Apps</strong>
              <p>
                Launch and switch app windows with just a few keystrokes. Quit running ones, copy details, search menu
                items and more.
              </p>
            </div>
            <div className={styles.image}>
              <div className={styles.imageWrapper}>
                <Image
                  src={LaunchApps}
                  width={846}
                  height={571}
                  layout="fixed"
                  alt="Example of Raycast window with an item with a keyboard shortcut set to launch it"
                />
              </div>
            </div>
          </Panel>
          <Panel rowEndSpan={7}>
            <div className={classNames(styles.panelIcon, styles.red)}>
              <CopyClipboard />
            </div>
            <strong>Clipboard History</strong>
            <p>
              Search for text, images, files, links and colors that you copied earlier and paste it again. Pin your
              frequently used items to access them quicker.
            </p>
          </Panel>
          <Panel rowEndSpan={7}>
            <div className={classNames(styles.panelIcon, styles.red)}>
              <Snippets />
            </div>
            <strong>Snippets</strong>
            <p>
              Write faster by inserting frequently typed text such as canned emails, code pieces and more. Use keywords
              to auto-expand snippets in-place.
            </p>
          </Panel>
          <Panel rowEndSpan={{ lg: 10, sm: 16 }} colEndSpan={{ lg: 3, md: 2 }} className={styles.mySchedule}>
            <div className={styles.image}>
              <div className={styles.imageWrapper}>
                <Image
                  src={MySchedule}
                  width={846}
                  height={571}
                  layout="fixed"
                  alt="A screenshot of Raycast showing the My Schedule command"
                />
              </div>
            </div>
            <div className={styles.text}>
              <div className={styles.panelIcon}>
                <Image src={MyScheduleIcon} width={32} height={32} layout="fixed" alt="Calendar Icon" />
              </div>
              <strong>My Schedule</strong>
              <p>
                Never miss another meeting. Join Zoom, Google Meet, Around meetings, and more, without digging around
                for a link and jump straight in to your calls. Check your upcoming schedule, block focus time or get an
                overview of your daily plan.
              </p>
              <Image src={MyScheduleIcons} width={224} height={32} layout="fixed" alt="list of meeting service icons" />
            </div>
          </Panel>
        </div>
      </Container>
      <BlockCallToAction href="https://raycast.com/manual" target="_blank" rel="noreferrer">
        Explore the manual
      </BlockCallToAction>
    </>
  );
};

export default Features;
